// Decides whether a page's rendered text actually reaches the page edge.
//
// The overflow detector (react-detectable-overflow) compares scroll metrics,
// which fire on pages whose text still renders entirely inside the page —
// confirmed false positives in print orders. The print backend
// (mobile-backend docker/pdfpages detection.js, measureTextEdgeProximity)
// only treats overflow as a customer-facing problem when the text comes
// within THRESHOLD_PERCENT of the page edge; this module applies the same
// rule to the in-app warning icon.
//
// Text bounds are measured with DOM ranges over text nodes for tight glyph
// bounds — element boxes often span the full page and would always look
// "near the edge".

const THRESHOLD_PERCENT = 3;

// Overflow smaller than this past a clipping ancestor is descender/rounding
// noise (the overflow tolerance hook already filters 5px); beyond it,
// roughly half a text line is visibly cut off.
const CLIP_TOLERANCE_PX = 12;

/**
 * Pure decision: given the page bounds and the rendered text bounds, is any
 * text within thresholdPercent of the page's bottom or right edge (or past
 * it)?
 *
 * @param {Object} options
 * @param {DOMRect|Object} options.pageRect - Page container bounding rect
 * @param {Array<DOMRect|Object>} options.textRects - Bounding rects of text ranges
 * @param {number} options.thresholdPercent - "Near edge" distance as % of page size
 * @returns {{ measured: boolean, nearEdge: boolean, distanceFromBottom: number|null, distanceFromRight: number|null }}
 */
export function evaluateTextEdgeProximity({
  pageRect,
  textRects,
  thresholdPercent = THRESHOLD_PERCENT,
}) {
  const unmeasured = {
    measured: false,
    nearEdge: false,
    distanceFromBottom: null,
    distanceFromRight: null,
  };

  if (!pageRect || pageRect.width === 0 || pageRect.height === 0) {
    return unmeasured;
  }

  let maxBottom = -Infinity;
  let maxRight = -Infinity;

  for (const rect of textRects || []) {
    if (rect.width === 0 && rect.height === 0) continue;
    maxBottom = Math.max(maxBottom, rect.bottom);
    maxRight = Math.max(maxRight, rect.right);
  }

  if (maxBottom === -Infinity) {
    return unmeasured;
  }

  const thresholdY = (pageRect.height * thresholdPercent) / 100;
  const thresholdX = (pageRect.width * thresholdPercent) / 100;
  const distanceFromBottom = pageRect.bottom - maxBottom;
  const distanceFromRight = pageRect.right - maxRight;

  return {
    measured: true,
    // Negative distance = text extends beyond the page
    nearEdge: distanceFromBottom < thresholdY || distanceFromRight < thresholdX,
    distanceFromBottom,
    distanceFromRight,
  };
}

/**
 * Is this text rect visually truncated by an ancestor with overflow
 * hidden/clip (e.g. photo-title-story's fixed-size box)? Range rects report
 * layout geometry, which extends past a clip boundary, so comparing them to
 * the clipping ancestor's box reveals text that prints cut off mid-page.
 */
function isClippedByAncestor(rect, startElement, pageContainer) {
  let ancestor = startElement;
  while (ancestor && ancestor !== pageContainer) {
    const style = window.getComputedStyle(ancestor);
    const clips = [style.overflow, style.overflowY, style.overflowX].some(
      (value) => value === "hidden" || value === "clip"
    );

    if (clips) {
      const ancestorRect = ancestor.getBoundingClientRect();
      if (
        rect.bottom > ancestorRect.bottom + CLIP_TOLERANCE_PX ||
        rect.right > ancestorRect.right + CLIP_TOLERANCE_PX
      ) {
        return true;
      }
    }
    ancestor = ancestor.parentElement;
  }
  return false;
}

/**
 * Collect tight text bounds inside a page container via ranges over its
 * visible text nodes, noting whether any text is clipped by an ancestor.
 */
function collectTextRects(pageContainer) {
  const rects = [];
  let anyClipped = false;
  const walker = document.createTreeWalker(pageContainer, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.textContent || node.textContent.trim().length === 0) continue;

    const parent = node.parentElement;
    if (!parent) continue;
    const style = window.getComputedStyle(parent);
    if (style.display === "none" || style.visibility === "hidden") continue;

    const range = document.createRange();
    range.selectNodeContents(node);
    const rect = range.getBoundingClientRect();
    rects.push(rect);

    if (!anyClipped && isClippedByAncestor(rect, parent, pageContainer)) {
      anyClipped = true;
    }
  }

  return { rects, anyClipped };
}

/**
 * DOM entry point: should this page's overflow actually warn the user?
 * Fails open — if the measurement is impossible (no container, zero-size
 * layout), the caller keeps the legacy behavior of showing the warning.
 *
 * @param {Element|null} pageContainer - The page's container element
 * @param {number} thresholdPercent - "Near edge" distance as % of page size
 * @returns {boolean} true when text reaches the edge OR measurement failed
 */
export function isTextNearPageEdge(
  pageContainer,
  thresholdPercent = THRESHOLD_PERCENT
) {
  if (!pageContainer) return true;

  try {
    const { rects, anyClipped } = collectTextRects(pageContainer);
    if (anyClipped) return true;

    const result = evaluateTextEdgeProximity({
      pageRect: pageContainer.getBoundingClientRect(),
      textRects: rects,
      thresholdPercent,
    });
    return result.measured ? result.nearEdge : true;
  } catch (e) {
    return true;
  }
}
